import classNames from "classnames";
import Button from "components/base/Button";
import axiosInstance from "helpers/api";
import { useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import { useForm, SubmitHandler } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";

type Inputs = {
  email: string;
};

const ForgotPasswordForm = ({
  layout,
}: {
  layout?: "simple" | "card" | "split";
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Inputs>();

  const [isLoading, setIsLoading] = useState(false);

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.post("/auth/forget-password", {
        email: data.email,
      });
      toast.success(response.data.message);
    } catch (error: any) {
      console.error(
        "Reset Password Failed:",
        error.response?.data || error.message
      );
      toast.error(error.response.data.message);
    }
    setIsLoading(false);
    reset();
  };

  const navigate = useNavigate();

  // NOTE: the legacy "if logged in, bounce to /dashboard" useEffect was
  // removed — it redirected any authenticated user hitting the forgot
  // password page to a route that doesn't exist on the AISearchPortal
  // domain, which then fell through the `*` catch-all back to "/".
  // A logged-in user still has a legitimate reason to reset their
  // password (forgot + stored session), so there's no reason to block.

  const goToLogin = () => {
    navigate("/agent-portal/login");
  };

  return (
    <div>
      <div
        className={classNames("text-center")}
      >
        <h4 className="text-body-highlight">Forgot your password?</h4>
        <p className="text-body-tertiary">
          Enter your email below and we will send <br className="d-sm-none" />
          you a reset link
        </p>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Control
            type="email"
            id="email"
            className="flex-1 mb-3"
            placeholder="Email"
            {...register("email", { required: true, maxLength: 80 })}
          />
          {errors.email?.type === "required" && (
              <p role="alert" className="text-danger fs-9" style={{ textAlign: "left"}}>
                Email is required
              </p>
            )}

          <div className="position-relative">
            <Button
              variant="primary"
              type="submit"
              className="w-100 border-0"
              disabled={isLoading}
              aria-label="Request reset link button"
              style={{
                background: isLoading
                  ? 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)'
                  : 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
                borderRadius: '12px',
                padding: '12px 20px',
                fontSize: '0.9rem',
                fontWeight: '600',
                boxShadow: isLoading
                  ? 'none'
                  : '0 8px 25px rgba(0, 212, 255, 0.3), 0 0 20px rgba(0, 212, 255, 0.2)',
                transition: 'all 0.3s ease',
                position: 'relative',
                overflow: 'hidden'
              }}
            >
              {/* Button glow effect */}
              {!isLoading && (
                <div 
                  className="position-absolute w-100 h-100 top-0 start-0"
                  style={{
                    background: 'linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.1) 50%, transparent 100%)',
                    animation: 'ai-shimmer 2s infinite'
                  }}
                />
              )}
              
              <div className="d-flex align-items-center justify-content-center position-relative">
                {isLoading ? (
                  <>
                    <Spinner animation="border" role="status" size="sm" className="me-2" />
                    <span>Sending Link...</span>
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPaperPlane} className="me-2" />
                    <span>Request Reset Link</span>
                  </>
                )}
              </div>
            </Button>
          </div>
          <Button
            type="button"
            variant="link"
            className="w-100 mt-3 text-center"
            disabled={isLoading}
            onClick={goToLogin}
          >
            Back to login
          </Button>
        </Form>
        {/* <Link to="#!" className="fs-9 fw-bold">
          Still having problems?
        </Link> */}
      </div>
      {/* AI Animation Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
          @keyframes ai-shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `
      }} />
    </div>
  );
};

export default ForgotPasswordForm;
